/* DashClip Personalization Studio — state, variant resolution, cart, proofs */
(function () {
  const root = document.querySelector('.dashclip-studio');
  if (!root) return;

  const configEl = root.querySelector('script[type="application/json"]') ||
    document.getElementById('DashclipConfig-' + root.dataset.sectionId);
  // Single-file studio: all family products live in one config document.
  // Shared settings (routes, money format, bulk rules) stay at the root;
  // `config` always points at the active product merged over the shared root.
  const shared = JSON.parse(configEl.textContent);
  const products = shared.products || [shared]; // single-product configs normalize to 1 item

  const PRODUCT_DEFAULTS = {
    family: '',
    kicker: '',
    copy: '',
    material: '',
    materialNote: '',
    sampleWeight: '',
    stageWord: '',
    finishMaterials: {},
    finishHeading: 'Choose your finish',
    finishNote: 'Screen colors are approximate.',
    finishSelector: 'swatches',
    baseName: '',
    artworkCopy: '',
    priceNote: '',
    designOrder: 'artwork-first',
    priceOnRequest: false,
    photos: [],
    photosHeading: 'A closer look.',
    photosNote: '',
    photosEyebrow: '',
    rules: {
      logoIncluded: false,
      logoUpcharge: 0,
      textUpcharge: 0,
      textMaxLength: 15,
      doubleSidedUpcharge: 0,
      supportsBackArt: false,
      supportsArtwork: true,
    },
    engraveRegion: { widthFrac: 1 },
  };

  function normalizeProductConfig(p) {
    const out = { ...PRODUCT_DEFAULTS, ...p };
    out.rules = { ...PRODUCT_DEFAULTS.rules, ...(p.rules || {}) };
    out.photos = (Array.isArray(out.photos) ? out.photos : []).map((ph) =>
      typeof ph === 'string' ? { url: ph, alt: '' } : ph
    );
    if (!out.baseName) out.baseName = out.productTitle || out.family || '';
    if (!out.photosEyebrow) out.photosEyebrow = (out.family || '').toUpperCase();
    if (!out.engraveRegion) out.engraveRegion = { ...PRODUCT_DEFAULTS.engraveRegion };
    return out;
  }

  for (let i = 0; i < products.length; i++) products[i] = normalizeProductConfig(products[i]);
  let config = { ...shared, ...products[0] };

  const $ = (sel) => root.querySelector(sel);
  const $$ = (sel) => Array.from(root.querySelectorAll(sel));

  /* ---------- money ---------- */
  function formatWithDelimiters(cents, precision = 2, thousands = ',', decimal = '.') {
    const value = (Number(cents) / 100).toFixed(precision);
    const parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts[0] + (parts[1] ? decimal + parts[1] : '');
  }

  function money(cents) {
    if (window.Shopify && Shopify.formatMoney) return Shopify.formatMoney(cents, config.moneyFormat);
    const format = config.moneyFormat || '${{amount}}';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return '$' + formatWithDelimiters(cents);
    const formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
    };
    return format.replace(match[0], formatWithDelimiters(cents, ...(formats[match[1]] || formats.amount)));
  }

  /* ---------- state ---------- */
  let activeIndex = 0;
  let finishes = [];
  let engravingValues = [];
  let profileValues = [];
  // capabilities — recomputed whenever the active product changes
  let supportsArtwork = true;
  let supportsText = true;
  let supportsBack = false;

  const MAX_UPLOAD_BYTES = 12 * 1024 * 1024;
  const WHOLESALE_FILE_TYPES = new Set([
    'image/png', 'image/jpeg', 'image/webp', 'image/svg+xml', 'application/pdf',
  ]);

  const state = {
    finish: null,
    profile: null,
    side: 'front',
    doubleSided: false,
    artwork: {
      front: { dataUrl: null, rawDataUrl: null, proofUrl: null, name: null, invert: false, size: 64 },
      back: { dataUrl: null, rawDataUrl: null, proofUrl: null, name: null, invert: false, size: 64 },
    },
    // Files kept for cart upload (not serialized into proof links)
    artworkFiles: { front: null, back: null },
    text: {
      front: { enabled: false, text: '', font: 'Canela', fontLabel: 'Classic' },
      back: { enabled: false, text: '', font: 'Canela', fontLabel: 'Classic' },
    },
    quantity: 1,
  };

  /* ---------- capabilities ----------
     Driven by the active product's config rules (metafields via Liquid /
     mock JSON in preview) — never by family name. Each control only
     renders or activates when the product's rules support it. */
  // back side may carry artwork only when supported and unlocked (free or paid)
  const backEnabled = () =>
    supportsBack && (state.doubleSided || config.rules.doubleSidedUpcharge === 0);
  const curText = () => state.text[state.side];
  const sideHasText = (sd) =>
    supportsText && state.text[sd].enabled && state.text[sd].text.trim().length > 0 &&
    (sd === 'front' || backEnabled());
  // mirrors LAYOUT_KINDS in dashclip-studio-3d.js — only 'maverick' → 'wallet'
  const FAMILY_KIND = { dashtag: 'tag', ballmarker: 'marker', maverick: 'wallet', alexander: 'alexander', hudson: 'hudson' };
  const familyKind = () =>
    FAMILY_KIND[String(config.family || '').toLowerCase().replace(/[^a-z]/g, '')] || '';

  /* ---------- active product ---------- */
  function handleize(s) {
    return String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function tabIndex(tab) {
    return products.findIndex((p) =>
      p.productHandle === tab.dataset.handle ||
      p.productHandle === tab.dataset.kind ||
      handleize(p.family) === tab.dataset.kind
    );
  }

  function renderSwatches() {
    const wrap = $('#dc-swatches');
    if (!wrap) return;
    wrap.replaceChildren();
    // select-style finish picking (e.g. Maverick): dropdown instead of swatches
    wrap.hidden = config.finishSelector === 'select';
    if (wrap.hidden) return;
    const seen = new Set();
    for (const v of config.variants) {
      const finish = v.options[0];
      if (seen.has(finish)) continue;
      seen.add(finish);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'swatch';
      btn.dataset.finish = finish;
      btn.setAttribute('aria-label', finish + (v.sku ? ' · ' + v.sku : ''));
      btn.setAttribute('aria-pressed', 'false');
      btn.title = finish;
      const chip = document.createElement('span');
      chip.className = 'chip';
      const color = swatchColorOf(v) || '#888888';
      chip.style.background = color;
      chip.style.setProperty('--check', checkColor(color));
      btn.appendChild(chip);
      btn.addEventListener('click', () => { state.finish = finish; syncUI(); });
      wrap.appendChild(btn);
    }
  }

  // dark check on light chips, light check on dark chips
  function checkColor(color) {
    let m = /rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)/.exec(color);
    let r, g, b;
    if (m) [r, g, b] = m.slice(1).map(Number);
    else {
      m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(color);
      if (!m) return '#fff';
      [r, g, b] = m.slice(1).map((x) => parseInt(x, 16));
    }
    return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? '#10253f' : '#fff';
  }

  function renderProfileSelect() {
    const wrapEl = $('#dc-thickness-control');
    const sel = $('#dc-marker-thickness');
    if (!wrapEl || !sel) return;
    wrapEl.hidden = !profileValues.length;
    sel.replaceChildren();
    for (const value of profileValues) {
      const o = document.createElement('option');
      o.value = value;
      o.textContent = value;
      sel.appendChild(o);
    }
    sel.value = state.profile || profileValues[0] || '';
  }

  function renderWalletPicker() {
    const wrapEl = $('#dc-wallet-picker');
    const sel = $('#dc-wallet-select');
    if (!wrapEl || !sel) return;
    sel.replaceChildren();
    const label = $('#dc-wallet-select-label');
    if (config.finishSelector === 'select') {
      // finishes are separate SKUs — pick via dropdown instead of swatches
      wrapEl.hidden = false;
      if (label) label.textContent = config.finishHeading || 'Choose your finish';
      for (const finish of finishes) {
        const rep = config.variants.find((v) => v.options[0] === finish);
        const o = document.createElement('option');
        o.value = finish;
        o.textContent = config.productTitle + ' — ' + finish + (rep && rep.sku ? ' — ' + rep.sku : '');
        sel.appendChild(o);
      }
      sel.value = state.finish;
      return;
    }
    const wallets = config.walletProducts || [];
    wrapEl.hidden = !wallets.length;
    if (label) label.textContent = 'Choose a separate wallet product';
    const ph = document.createElement('option');
    ph.value = '';
    ph.textContent = 'Select…';
    sel.appendChild(ph);
    for (const w of wallets) {
      const o = document.createElement('option');
      const idx = products.findIndex((p) => p.productHandle === w.handle);
      o.value = idx >= 0 ? 'tab:' + idx : w.url;
      o.textContent = w.title;
      sel.appendChild(o);
    }
    sel.value = '';
  }

  function renderPhotoBand() {
    const section = $('#dc-photos');
    if (!section) return;
    // legacy single-product configs leave the band to Liquid's server render
    if (!shared.products) return;
    const photos = config.photos || [];
    section.hidden = !photos.length;
    if (!photos.length) return;
    const eyebrow = section.querySelector('.photo-heading .eyebrow');
    if (eyebrow) eyebrow.textContent = config.photosEyebrow || (config.family || '').toUpperCase();
    const title = $('#dc-photos-title');
    if (title) title.textContent = config.photosHeading || 'A closer look.';
    const note = section.querySelector('.photo-heading p');
    if (note) {
      note.textContent = config.photosNote || '';
      note.hidden = !config.photosNote;
    }
    const pair = section.querySelector('.photo-pair');
    if (pair) {
      pair.replaceChildren();
      photos.forEach((photo) => {
        const fig = document.createElement('figure');
        const img = document.createElement('img');
        img.src = photo.url;
        img.alt = photo.alt || '';
        img.width = img.height = 1254;
        img.loading = 'lazy';
        const cap = document.createElement('figcaption');
        cap.textContent = photo.caption || photo.alt || '';
        fig.append(img, cap);
        pair.appendChild(fig);
      });
    }
  }

  // shrink the decorative stage word only if it would overflow one line
  function fitStageWord() {
    const word = $('#dc-stage-word');
    const stage = word && word.parentElement;
    if (!word || !stage) return;
    word.style.fontSize = '';
    const baseSize = parseFloat(getComputedStyle(word).fontSize);
    const cs = getComputedStyle(stage);
    const avail = stage.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    const width = word.getBoundingClientRect().width;
    if (!baseSize || !avail || !width) return;
    const ratio = (avail * 0.96) / width;
    if (ratio < 1) word.style.fontSize = (baseSize * ratio).toFixed(1) + 'px';
  }

  // update the parts of the DOM that come from the product record
  function renderProductChrome() {
    const set = (sel, text) => { const el = $(sel); if (el) el.textContent = text; };
    set('#dc-product-kicker', config.kicker || '');
    set('#dc-product-title', config.productTitle || '');
    set('#dc-product-copy', config.copy || '');
    set('#dc-stage-title', 'THE ' + (config.productTitle || '').toUpperCase());
    set('#dc-stage-word', ((config.stageWord || config.productTitle || '').toUpperCase() + '.').replace(/^\.+$/, 'YOURS.'));
    fitStageWord();
    set('#dc-material-note', config.materialNote || '');
    set('#dc-finish-heading', config.finishHeading || 'Choose your finish');
    set('#dc-finish-note', config.finishNote || 'Screen colors are approximate.');
    const selMode = config.finishSelector === 'select';
    set('#dc-finish-count', finishes.length + ' ' + (selMode ? 'products' : finishes.length === 1 ? 'finish' : 'finishes'));
    // text-first products (Alexander) show personalization before artwork
    const designOpts = $('#dc-design-options');
    const artEl = $('#dc-customization-section');
    const textEl = $('#dc-personalization-section');
    if (designOpts && artEl && textEl) {
      if (config.designOrder === 'text-first') designOpts.appendChild(artEl);
      else designOpts.insertBefore(artEl, textEl);
    }
    renderSwatches();
    renderProfileSelect();
    renderWalletPicker();
    renderPhotoBand();
    const nameInput = $('#dc-name-input');
    if (nameInput) nameInput.maxLength = config.rules.textMaxLength || 0;
  }

  function activateProduct(index) {
    activeIndex = Math.max(0, Math.min(index, products.length - 1));
    config = { ...shared, ...products[activeIndex] };
    finishes = [...new Set(config.variants.map((v) => v.options[0]))];
    engravingValues = config.options[1] ? config.options[1].values : [];
    profileValues = config.options[2] ? config.options[2].values : [];
    supportsArtwork = config.rules.supportsArtwork !== false;
    supportsText = config.rules.textMaxLength > 0;
    supportsBack = !!(config.rules.supportsBackArt || config.rules.doubleSidedUpcharge > 0);

    // product-scoped state resets; user content (artwork, text, qty) persists across tabs
    state.finish = finishes[0];
    state.profile = profileValues[0] || null;
    state.doubleSided = false;
    state.side = 'front';

    ['#dc-checkout-flow', '#dc-order-step', '#dc-proof-banner'].forEach((sel) => {
      const el = $(sel);
      if (el) el.hidden = true;
    });
    lockOrderStep();

    $$('.category-tabs .family-tab').forEach((tab) => {
      const active = tabIndex(tab) === activeIndex;
      tab.classList.toggle('active', active);
      tab.setAttribute('aria-pressed', String(active));
    });
    renderProductChrome();
    syncUI();
  }

  /* ---------- engraving option resolution ----------
     Merchants define Engraving option values per family, e.g.
     "Logo", "Logo + Name", "1 side", "Both sides", "Blank", "Name".
     We score each candidate against the flags the design needs. */
  function desiredFlags() {
    return {
      name: sideHasText('front') || sideHasText('back'),
      double: state.doubleSided,
      logo: supportsArtwork && !!(state.artwork.front.dataUrl || state.artwork.back.dataUrl),
    };
  }

  function bestEngravingValue() {
    if (!engravingValues.length) return null;
    const flags = desiredFlags();
    let best = engravingValues[0];
    let bestScore = -1;
    for (const value of engravingValues) {
      const v = value.toLowerCase();
      const hasName = /name|text|monogram|personal/i.test(v);
      const hasDouble = /both|double|2 ?sides|two/i.test(v);
      const hasLogo = /logo|mark|artwork|crest/i.test(v);
      const isBlank = /blank|none/i.test(v);
      let score = 0;
      if (flags.name === hasName) score += 2;
      if (flags.double === hasDouble) score += 2;
      // prefer logo vs blank matching only when family distinguishes them
      if (hasLogo || isBlank) score += flags.logo === hasLogo ? 1 : 0;
      if (score > bestScore) { bestScore = score; best = value; }
    }
    return best;
  }

  function resolveVariant() {
    const target = [state.finish, bestEngravingValue(), state.profile].filter(Boolean);
    const available = config.variants.filter((v) => v.available);
    const pool = available.length ? available : config.variants;
    let match = pool.find((v) =>
      v.options.slice(0, target.length).every((o, i) => o === target[i])
    );
    if (!match) {
      match = pool.find((v) => v.options[0] === state.finish) || config.variants[0];
    }
    return match;
  }

  // variant metafield swatch_color wins; else the Finish option's native swatch
  function swatchColorOf(v) {
    return (v && v.swatchColor) || (config.swatches && config.swatches[v.options[0]]) || null;
  }

  function baseVariant() {
    // cheapest in-stock variant with the same finish — used to decompose upcharges
    const sameFinish = config.variants.filter((v) => v.options[0] === state.finish);
    const available = sameFinish.filter((v) => v.available);
    return (available.length ? available : sameFinish)
      .sort((a, b) => a.price - b.price)[0] || config.variants[0];
  }

  /* ---------- 3D bridge ---------- */
  function emitUpdate() {
    const resolved = resolveVariant();
    const variant = resolved
      ? { ...resolved, swatchColor: swatchColorOf(resolved) }
      : resolved;
    window.dispatchEvent(new CustomEvent('dashclip:update', {
      detail: {
        finish: state.finish,
        side: state.side,
        doubleSided: state.doubleSided,
        artwork: {
          front: { ...state.artwork.front },
          back: { ...state.artwork.back },
        },
        text: { front: { ...state.text.front }, back: { ...state.text.back } },
        variant,
        product: {
          family: config.family,
          rules: config.rules,
          engraveRegion: config.engraveRegion,
          sampleWeight: config.sampleWeight,
          material: config.material,
          finishMaterials: config.finishMaterials,
        },
      },
    }));
  }

  /* ---------- UI sync ---------- */
  function syncUI() {
    if (!config.variants.length) return;
    const variant = resolveVariant();
    const base = baseVariant();
    const flags = desiredFlags();

    $('#dc-product-code').textContent = variant.sku || '';
    $('#dc-stage-sku').textContent = ((variant.sku || '') + ' / ' + state.finish).toUpperCase();
    $('#dc-finish-name').textContent = state.finish;
    const fmKey = Object.keys(config.finishMaterials || {})
      .find((k) => k.toLowerCase() === String(state.finish || '').toLowerCase());
    $('#dc-finish-material').textContent =
      (fmKey && config.finishMaterials[fmKey]) || variant.material || config.material || config.materialNote || '';
    $('#dc-retail-unit-price').textContent = money(variant.price);
    $('#dc-retail-base-price').textContent = money(base.price);

    // cost rows: decompose variant price vs base into logo / text / double-side
    const diff = variant.price - base.price;
    const logoCharge = !config.rules.logoIncluded && flags.logo ? config.rules.logoUpcharge : 0;
    const textCharge = flags.name ? Math.max(diff - logoCharge, 0) : 0;
    const dsCharge = Math.max(diff - logoCharge - textCharge, 0);
    $('#dc-text-charge').textContent = money(textCharge);
    $('#dc-double-sided-row').hidden = !state.doubleSided;
    $('#dc-double-sided-charge').textContent = money(dsCharge);
    // price-on-request products hide all base/subtotal price chrome
    const showPricing = !config.priceOnRequest;
    const priceWrap = $('#dc-retail-price-wrap');
    if (priceWrap) priceWrap.hidden = !showPricing;
    const baseRow = $('#dc-retail-base-row');
    if (baseRow) baseRow.hidden = !showPricing;
    const subtotalRow = $('#dc-retail-subtotal-row');
    if (subtotalRow) subtotalRow.hidden = !showPricing;
    $('#dc-logo-charge-row').hidden = config.rules.logoIncluded || !supportsArtwork || !showPricing;
    $('#dc-logo-charge').textContent = money(logoCharge);
    const baseName = config.baseName || config.family || config.productTitle;
    const fmt = (c) => money(c).replace(/\.00\b/, '');
    let note;
    if (!showPricing) {
      note = 'Product and artwork pricing confirmed separately.';
    } else if (config.rules.logoIncluded) {
      note = 'Logo customization is included in the ' + baseName + ' retail price.';
      if (supportsText && config.rules.textUpcharge > 0) note += ' Text personalization is an optional ' + fmt(config.rules.textUpcharge) + ' upgrade per item.';
    } else {
      note = baseName + ': ' + fmt(base.price) + '.';
      if (supportsArtwork && config.rules.logoUpcharge > 0) note += ' Optional logo customization: ' + fmt(config.rules.logoUpcharge) + '.';
      if (supportsText && config.rules.textUpcharge > 0) note += ' Optional name personalization: ' + fmt(config.rules.textUpcharge) + ' per item.';
    }
    $('#dc-review-pricing-note').textContent = note;
    $('#dc-text-charge-row') && ($('#dc-text-charge-row').hidden = !supportsText);

    /* ---------- capability-driven visibility ---------- */
    const editSide = $('.edit-side');
    if (editSide) {
      editSide.hidden = !supportsBack;
      const backBtn = editSide.querySelector('[data-side="back"]');
      if (backBtn) backBtn.disabled = config.rules.doubleSidedUpcharge > 0 && !state.doubleSided;
    }
    const sideNote = $('#dc-side-pricing-note');
    if (sideNote) sideNote.hidden = !(config.rules.doubleSidedUpcharge > 0);
    const artSection = $('#dc-customization-section');
    if (artSection) artSection.hidden = !supportsArtwork;
    const textSection = $('#dc-personalization-section');
    if (textSection) textSection.hidden = !supportsText;
    const engOpt = $('#dc-engraving-option');
    if (engOpt) {
      engOpt.hidden = !(config.rules.doubleSidedUpcharge > 0);
      const singleSideLabel = $('#dc-single-side-label');
      if (singleSideLabel) singleSideLabel.textContent = config.rules.logoIncluded
        ? 'Single-side logo engraving included'
        : 'Single-side engraving';
      const inc = engOpt.querySelector('.engraving-included span');
      if (inc) inc.textContent = money(base.price) + ' per item';
      const upg = engOpt.querySelector('.engraving-upgrade strong');
      if (upg) upg.textContent = '+ ' + money(config.rules.doubleSidedUpcharge) + ' per item';
      const engHelp = $('#dc-double-sided-help');
      if (engHelp) engHelp.textContent = (config.rules.logoIncluded
        ? 'Select to add artwork to the second side. '
        : 'Select to enable engraving on the second side. ') +
        money(base.price + config.rules.doubleSidedUpcharge) + ' per item with double-sided engraving.';
    }

    // pricing copy derived from rules (+ per-product overrides), not family name
    const priceNote = $('#dc-retail-price-note');
    if (priceNote) priceNote.textContent = config.priceNote || (config.rules.logoIncluded
      ? 'Retail price · includes logo customization'
      : 'Retail price · engraving options below');
    const baseLabel = $('#dc-retail-base-label');
    if (baseLabel) baseLabel.innerHTML = 'Retail price <small>per item</small>';
    const persPrice = $('#dc-personalization-price');
    if (persPrice) persPrice.textContent = config.rules.textUpcharge > 0
      ? '+ ' + money(config.rules.textUpcharge) + ' / item' : 'Included';
    if (supportsArtwork) {
      const fam = config.family || config.productTitle;
      const desc = config.artworkCopy || (config.rules.logoIncluded
        ? (config.rules.doubleSidedUpcharge > 0
          ? 'Your logo on one side is included in the ' + money(base.price) + ' retail price. Design both sides for an additional ' + fmt(config.rules.doubleSidedUpcharge) + ' per item.'
          : 'Your logo upload is included in the ' + fam + ' retail price. Add a club crest, company logo or personal graphic.')
        : 'Add your logo, club crest or graphic for ' + fmt(config.rules.logoUpcharge) + ' per item.');
      const descEl = $('#dc-customization-description');
      if (descEl) descEl.textContent = 'Customization · ' + desc.replace(/^Customization\s*·\s*/i, '');
      const custPrice = $('#dc-customization-price');
      if (custPrice) custPrice.textContent = config.rules.logoIncluded
        ? 'Included'
        : config.rules.logoUpcharge > 0 ? '+ ' + money(config.rules.logoUpcharge) + ' / item' : 'Optional';
    }

    // renumber the visible steps in display order (01 is always finish)
    let stepNo = 1;
    const stepOrder = config.designOrder === 'text-first'
      ? [['#dc-personalization-step', supportsText], ['#dc-customization-step', supportsArtwork]]
      : [['#dc-customization-step', supportsArtwork], ['#dc-personalization-step', supportsText]];
    stepOrder.concat([['#dc-quantity-step', true]]).forEach(([sel, visible]) => {
      const el = $(sel);
      if (el && visible) el.textContent = String(++stepNo).padStart(2, '0');
    });

    const qty = Math.max(1, state.quantity | 0);
    $('#dc-retail-subtotal-label').innerHTML = 'Subtotal <small>' + qty + (qty === 1 ? ' item' : ' items') + '</small>';

    const swatchEl = $('#dc-order-swatch');
    if (swatchEl) swatchEl.style.background = swatchColorOf(variant) || '#888888';
    const nameEl = $('#dc-order-product-name');
    if (nameEl) nameEl.textContent = [baseName, state.finish].filter(Boolean).join(' · ');
    const detailEl = $('#dc-order-product-detail');
    if (detailEl) detailEl.textContent = (variant.options || []).slice(1).filter(Boolean).join(' · ');
    const inclRow = $('#dc-logo-included-row');
    if (inclRow) inclRow.hidden = !(config.rules.logoIncluded && supportsArtwork && showPricing);
    $('#dc-retail-subtotal').textContent = money(variant.price * qty);

    const bulk = qty >= config.bulkThreshold;
    $('#dc-order-type').textContent = bulk ? 'Bulk order' : 'Individual order';

    // swatch active state
    $$('#dc-swatches .swatch').forEach((btn) => {
      const active = btn.dataset.finish === state.finish;
      btn.classList.toggle('active', active);
      btn.setAttribute('aria-pressed', active);
      const chip = btn.querySelector('.chip');
      if (chip) chip.textContent = active ? '✓' : '';
    });

    // side buttons
    $$('.edit-side [data-side]').forEach((btn) => {
      const active = btn.dataset.side === state.side;
      btn.classList.toggle('active', active);
      btn.setAttribute('aria-pressed', active);
    });

    // upload UI
    if (supportsArtwork) {
      const art = state.artwork[state.side];
      const hasSrc = !!(art.dataUrl || art.rawDataUrl);
      $('#dc-upload-label').textContent = art.dataUrl ? (art.name || 'Artwork selected') : 'Choose your artwork';
      $('#dc-upload-hint').textContent = art.dataUrl
        ? 'Click or drop to replace'
        : 'PNG, JPG or WebP · up to 12 MB · or drop it here';
      $('#dc-remove-logo').hidden = !hasSrc;
      $('#dc-sample-row').hidden = hasSrc;
      $('#dc-invert').disabled = !hasSrc;
      $('#dc-invert-wrap').classList.toggle('is-disabled', !hasSrc);
      $('#dc-invert').checked = art.invert;
      const showThumb = !!(art.dataUrl && art.rawDataUrl);
      const thumb = $('#dc-upload-thumb');
      thumb.toggleAttribute('hidden', !showThumb);
      $('#dc-upload-icon').toggleAttribute('hidden', showThumb); // SVG elements have no .hidden property
      $('#dc-upload-zone').classList.toggle('has-thumb', showThumb);
      if (showThumb && thumb.dataset.src !== art.rawDataUrl) { thumb.src = art.rawDataUrl; thumb.dataset.src = art.rawDataUrl; }
      const detectLine = $('#dc-art-confirm');
      const ask = !!(art.dataUrl && art.rawDataUrl && art.detected);
      detectLine.hidden = !ask;
      if (ask) {
        detectLine.classList.toggle('is-warning', !!art.error);
        $('#dc-art-confirm-icon').hidden = !art.error;
        $('#dc-art-confirm-text').textContent = art.error ||
          (art.detected === 'light' ? 'Light logo detected.' : 'Dark logo detected.');
        $('#dc-art-confirm-no').textContent = art.error
          ? 'Got it'
          : 'Wrong? Switch to ' + (art.detected === 'light' ? 'dark' : 'light');
      }
      // wallet back is a restricted strip under the clip — once text is added
      // the logo's height is capped, so the slider can't move it; lock it
      const sizeLocked = state.side === 'back' && familyKind() === 'wallet' &&
        curText().enabled && curText().text.trim().length > 0;
      $('#dc-art-size').disabled = !art.dataUrl || sizeLocked;
      $('#dc-art-size').value = art.size;
      setSizeFill();
      $('#dc-art-size-value').textContent = sizeLocked ? 'Fixed with text' : art.size + '%';
      const statusText = (!ask && art.error) ||
        (art.dataUrl
          ? art.warning || 'Artwork previewed as a single laser-engraving tone on the ' + state.side + ' side.'
          : '');
      $('#dc-upload-status-text').textContent = statusText;
      // only flag the status as a warning when IT carries the message —
      // an error already shown in the confirm line shouldn't icon this line
      const statusWarn = !!((!ask && art.error) || (art.dataUrl && art.warning));
      $('#dc-upload-status-icon').hidden = !statusWarn;
      $('#dc-upload-status').classList.toggle('is-warning', statusWarn);
    }

    // text UI
    if (supportsText) {
      const t = curText();
      $('#dc-personalize').checked = t.enabled;
      $('#dc-name-input').value = t.text;
      $('#dc-counter').textContent = t.text.length + ' / ' + config.rules.textMaxLength;
      const otherSide = state.side === 'front' ? 'back' : 'front';
      const other = state.text[otherSide];
      $('#dc-text-help').textContent =
        (other.enabled && other.text.trim()
          ? 'The ' + otherSide + ' side says “' + other.text + '”. '
          : '') +
        'Up to ' + config.rules.textMaxLength + ' characters — a name, nickname or initials.';
      $$('#dc-fonts button').forEach((btn) => {
        const active = btn.dataset.font === t.font;
        btn.classList.toggle('active', active);
        btn.setAttribute('aria-pressed', active);
      });
    }

    $('#dc-double-sided') && ($('#dc-double-sided').checked = state.doubleSided);
    $('#dc-quantity').value = qty;

    emitUpdate();
  }

  /* ---------- artwork ---------- */
  const uploadRevision = { front: 0, back: 0 };

  function loadImageEl(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      // R2-served artwork is cross-origin; CORS keeps the mask canvas readable.
      if (!src.startsWith('data:')) img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Image could not be loaded.'));
      img.src = src;
    });
  }

  // white/light background → transparent, dark strokes → opaque black mask.
  // invert: true = light logo, false = dark logo, null = auto-detect.
  // Returns { url, invert, auto } so callers can tell the user what was chosen.
  function prepareArtworkMask(image, invert) {
    if (image.width * image.height > 36000000)
      throw new Error('Please use an image smaller than 36 megapixels.');
    const k = Math.min(1, 1200 / Math.max(image.width, image.height));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(image.width * k));
    c.height = Math.max(1, Math.round(image.height * k));
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(image, 0, 0, c.width, c.height);
    const data = ctx.getImageData(0, 0, c.width, c.height);
    const px = data.data, W = c.width, H = c.height;
    const bw = Math.max(2, Math.round(Math.min(W, H) * 0.02));

    let litSum = 0, litN = 0;
    for (let i = 0; i < W * H; i++) {
      if (px[i * 4 + 3] > 128) {
        litSum += 0.2126 * px[i * 4] + 0.7152 * px[i * 4 + 1] + 0.0722 * px[i * 4 + 2];
        litN++;
      }
    }
    const avgLum = litN ? litSum / litN : 255;

    const analyse = (inv) => {
      const alpha = new Uint8ClampedArray(W * H);
      let minX = W, minY = H, maxX = -1, maxY = -1, edge = 0, edgeN = 0;
      for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
          const i = y * W + x, j = i * 4;
          const lum = 0.2126 * px[j] + 0.7152 * px[j + 1] + 0.0722 * px[j + 2];
          const a = Math.min(1, Math.max(0, ((inv ? lum : 255 - lum) - 16) / 195)) * (px[j + 3] / 255);
          alpha[i] = Math.round(a * 255);
          if (x < bw || y < bw || x >= W - bw || y >= H - bw) { edge += a; edgeN++; }
          if (a > 0.12) {
            minX = Math.min(minX, x); minY = Math.min(minY, y);
            maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
          }
        }
      }
      return { alpha, minX, minY, maxX, maxY, found: maxX >= minX, border: edge / edgeN };
    };

    const auto = invert == null;
    let inv = !!invert, res;
    if (auto) {
      const a = analyse(false), b = analyse(true);
      if (!a.found && !b.found)
        throw new Error("We couldn't find a logo shape in this image. Try a PNG with a transparent background, or a logo on a solid background that contrasts with it.");
      if (a.found && b.found)
        inv = Math.abs(a.border - b.border) > 0.2 ? b.border < a.border : avgLum > 150;
      else inv = !a.found;
      res = inv ? b : a;
    } else {
      res = analyse(inv);
      if (!res.found)
        throw Object.assign(new Error(inv
          ? 'Nothing was detected with the light-logo option — this artwork is already dark. Untick it to restore the preview.'
          : 'No artwork was detected. Tick "My artwork is white or light-colored" and try again.'), { kind: 'empty', inv });
      if (inv && res.border > 0.5)
        throw new Error('That option inverted the background — this artwork already reads as dark. Untick it to keep the normal preview.');
    }

    for (let i = 0; i < W * H; i++) {
      px[i * 4] = px[i * 4 + 1] = px[i * 4 + 2] = 0;
      px[i * 4 + 3] = res.alpha[i];
    }
    ctx.putImageData(data, 0, 0);
    const w = res.maxX - res.minX + 1, h = res.maxY - res.minY + 1;
    const k2 = Math.min(1, 1024 / Math.max(w, h));
    const out = document.createElement('canvas');
    out.width = Math.ceil(w * k2) + 4;
    out.height = Math.ceil(h * k2) + 4;
    out.getContext('2d').drawImage(c, res.minX, res.minY, w, h, 2, 2, out.width - 4, out.height - 4);
    return { url: out.toDataURL('image/png'), invert: inv, auto, border: res.border };
  }

  async function applyArtwork(src, name, side = state.side, invert = null) {
    const rev = ++uploadRevision[side];
    // stash the original up-front — if masking fails (e.g. a light logo with
    // invert off) the user needs the raw source to retry via the invert toggle
    const art = state.artwork[side];
    const prevRaw = art.rawDataUrl, prevInvert = art.invert;
    art.rawDataUrl = src;
    art.name = name;
    art.invert = !!invert;
    if (invert == null) art.detected = null;
    try {
      const img = await loadImageEl(src);
      const result = await prepareArtworkMask(img, invert);
      const mask = result.url;
      if (rev !== uploadRevision[side]) return;
      const maskImg = await loadImageEl(mask);
      if (rev !== uploadRevision[side]) return;
      const max = 480;
      const ratio = Math.min(max / maskImg.width, max / maskImg.height, 1);
      const thumb = document.createElement('canvas');
      thumb.width = Math.max(1, Math.round(maskImg.width * ratio));
      thumb.height = Math.max(1, Math.round(maskImg.height * ratio));
      const tctx = thumb.getContext('2d');
      tctx.fillStyle = '#fff'; // JPEG flattens alpha — composite on white for proofs
      tctx.fillRect(0, 0, thumb.width, thumb.height);
      tctx.drawImage(maskImg, 0, 0, thumb.width, thumb.height);
      Object.assign(art, {
        dataUrl: mask, rawDataUrl: src, name, invert: result.invert, error: null,
        warning: !result.auto && !result.invert && result.border > 0.5
          ? 'Showing it as a dark logo. If this looks like a solid block, tick "My artwork is white or light-colored".'
          : null,
        detected: result.auto ? (result.invert ? 'light' : 'dark') : null,
        proofUrl: /^https?:\/\//i.test(art.proofUrl || src)
          ? (art.proofUrl || src)
          : thumb.toDataURL('image/jpeg', 0.7),
      });
      syncUI();
    } catch (e) {
      if (rev !== uploadRevision[side]) return;
      if (prevRaw !== src) { art.dataUrl = null; art.invert = false; art.detected = null; } // new source failed — don't leave the old artwork on the model
      else art.invert = prevInvert; // retry failed — checkbox reflects the mask still shown
      art.error = prevRaw === src && art.dataUrl && e.kind === 'empty'
        ? (e.inv
          ? "We couldn't find a light logo shape in this file — it looks dark, so we kept the dark-logo setting. If it's the wrong file, upload a different one."
          : "We couldn't find a dark logo shape in this file — it looks white or light on a transparent background, so we kept the light-logo setting. If it's the wrong file, upload a different one.")
        : e.message || 'Could not read that image.';
      syncUI(); // reveal the invert toggle so the light-logo retry is reachable
    }
  }

  const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
  function handleArtworkFile(file) {
    if (!file) return;
    const art = state.artwork[state.side];
    if (!ACCEPTED_TYPES.includes(file.type)) {
      art.error = 'Please choose a PNG, JPG or WebP image.';
      syncUI();
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      art.error = 'File is larger than 12 MB — please choose a smaller image.';
      syncUI();
      return;
    }
    state.artworkFiles[state.side] = file;
    const reader = new FileReader();
    reader.onload = () => applyArtwork(reader.result, file.name);
    reader.readAsDataURL(file);
  }

  const logoInput = $('#dc-logo-input');
  if (logoInput) {
    logoInput.addEventListener('change', () => {
      handleArtworkFile(logoInput.files[0]);
      logoInput.value = ''; // lets the same file be picked again
    });
  }

  const uploadBox = $('#dc-upload-box');
  if (uploadBox) {
    let dragDepth = 0;
    const hasFiles = (e) => Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes('Files');
    uploadBox.addEventListener('dragenter', (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth++;
      uploadBox.classList.add('is-dragover');
    });
    uploadBox.addEventListener('dragover', (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    });
    uploadBox.addEventListener('dragleave', () => {
      dragDepth = Math.max(0, dragDepth - 1);
      if (!dragDepth) uploadBox.classList.remove('is-dragover');
    });
    uploadBox.addEventListener('drop', (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth = 0;
      uploadBox.classList.remove('is-dragover');
      handleArtworkFile(e.dataTransfer.files[0]);
    });
  }

  const removeBtn = $('#dc-remove-logo');
  if (removeBtn) {
    removeBtn.addEventListener('click', () => {
      state.artwork[state.side] = { dataUrl: null, rawDataUrl: null, proofUrl: null, name: null, invert: false, error: null, detected: null, size: 64 };
      state.artworkFiles[state.side] = null;
      if (logoInput) logoInput.value = '';
      syncUI();
    });
  }

  const sampleBtn = $('#dc-sample');
  if (sampleBtn) {
    sampleBtn.addEventListener('click', async () => {
      try {
        const res = await fetch(config.sampleArtwork);
        const blob = await res.blob();
        const dataUrl = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result);
          fr.onerror = reject;
          fr.readAsDataURL(blob);
        });
        state.artworkFiles[state.side] = null;
        applyArtwork(dataUrl, 'DashClip mark');
      } catch (e) {
        const c = document.createElement('canvas');
        c.width = c.height = 512;
        const ctx = c.getContext('2d');
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 18;
        ctx.beginPath();
        ctx.arc(256, 256, 190, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = '#000';
        ctx.font = '700 200px Georgia, serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('DC', 256, 268);
        state.artworkFiles[state.side] = null; // generated; serialized via dataUrl
        applyArtwork(c.toDataURL('image/png'), 'DashClip mark');
      }
    });
  }

  $('#dc-art-confirm-no')?.addEventListener('click', () => {
    const art = state.artwork[state.side];
    if (art.error) { art.error = null; syncUI(); return; }
    if (art.rawDataUrl) applyArtwork(art.rawDataUrl, art.name, state.side, !art.invert);
  });

  const invertEl = $('#dc-invert');
  if (invertEl) invertEl.addEventListener('change', () => {
    const art = state.artwork[state.side];
    if (art.rawDataUrl) applyArtwork(art.rawDataUrl, art.name, state.side, invertEl.checked);
    else { art.invert = invertEl.checked; emitUpdate(); }
  });

  const sizeEl = $('#dc-art-size');
  function setSizeFill() {
    const el = $('#dc-art-size');
    if (!el) return;
    const min = +el.min || 0;
    const max = +el.max || 100;
    el.style.setProperty('--dc-fill', ((el.value - min) / (max - min)) * 100 + '%');
  }
  setSizeFill();
  if (sizeEl) sizeEl.addEventListener('input', () => {
    setSizeFill();
    state.artwork[state.side].size = +sizeEl.value;
    $('#dc-art-size-value').textContent = sizeEl.value + '%';
    emitUpdate();
  });

  /* ---------- controls ---------- */
  const thicknessEl = $('#dc-marker-thickness');
  if (thicknessEl) thicknessEl.addEventListener('change', () => { state.profile = thicknessEl.value; syncUI(); });

  const walletSelect = $('#dc-wallet-select');
  if (walletSelect) walletSelect.addEventListener('change', () => {
    if (walletSelect.value.startsWith('tab:')) {
      activateProduct(+walletSelect.value.slice(4));
      return;
    }
    if (config.finishSelector === 'select') {
      state.finish = walletSelect.value;
      syncUI();
      return;
    }
    try { sessionStorage.setItem('dashclip-handoff', JSON.stringify(serializeDesign())); } catch (e) {}
    if (walletSelect.value) window.location.href = walletSelect.value;
  });

  const dsEl = $('#dc-double-sided');
  if (dsEl) dsEl.addEventListener('change', () => {
    state.doubleSided = dsEl.checked;
    if (!dsEl.checked && config.rules.doubleSidedUpcharge > 0) {
      if (state.side === 'back') state.side = 'front';
    }
    syncUI();
  });

  $$('.edit-side [data-side]').forEach((btn) =>
    btn.addEventListener('click', () => {
      if (btn.dataset.side === 'back' && (btn.disabled || !backEnabled())) return;
      state.side = btn.dataset.side;
      syncUI();
    })
  );

  const personalizeEl = $('#dc-personalize');
  if (personalizeEl) personalizeEl.addEventListener('change', () => {
    curText().enabled = personalizeEl.checked;
    syncUI();
  });

  const nameInput = $('#dc-name-input');
  if (nameInput) nameInput.addEventListener('input', () => {
    const t = curText();
    t.text = nameInput.value.slice(0, config.rules.textMaxLength);
    if (t.text.trim()) t.enabled = true;
    syncUI();
  });

  $$('#dc-fonts button').forEach((btn) =>
    btn.addEventListener('click', () => {
      const t = curText();
      t.font = btn.dataset.font;
      t.fontLabel = btn.querySelector('small')?.textContent || t.font;
      syncUI();
    })
  );

  const qtyEl = $('#dc-quantity');
  if (qtyEl) {
    const onQty = () => { state.quantity = Math.max(1, +qtyEl.value || 1); syncUI(); };
    qtyEl.addEventListener('input', onQty);
    qtyEl.addEventListener('change', onQty);
  }

  /* ---------- worker upload helpers ---------- */
  const workerBaseUrl = () => (config.workerUrl || '').replace(/\/+$/, '');

  // Helper: convert a data URL, remote URL, or Blob into a File object for upload.
  async function fileForUpload(source, filename, mimeType = 'image/png') {
    if (source instanceof File) return source;
    let blob = source instanceof Blob ? source : null;
    if (!blob && typeof source === 'string' && (/^data:/i.test(source) || /^https?:\/\//i.test(source))) {
      const res = await fetch(source);
      if (!res.ok) throw new Error('Artwork could not be retrieved.');
      blob = await res.blob();
    }
    if (!blob) return null;
    return new File([blob], filename, { type: blob.type || mimeType });
  }

  async function uploadToWorker(file, prefix) {
    const workerUrl = workerBaseUrl();
    if (!file || !workerUrl) return '';
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(workerUrl + '/upload?prefix=' + encodeURIComponent(prefix), {
      method: 'POST',
      body: fd,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => 'Upload failed');
      throw new Error(text || 'Upload failed');
    }
    const data = await res.json();
    return data.url || '';
  }

  // Store the current design's ?design= URL in R2, returning a short link.
  async function storeProofLink() {
    const workerUrl = workerBaseUrl();
    if (!workerUrl) return '';
    const res = await fetch(workerUrl + '/proof', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: buildProofUrl() }),
    });
    const data = await res.json().catch(() => null);
    return res.ok && data?.url ? data.url : '';
  }

  // Upload artwork to R2 (unless already uploaded) so it can be referenced by
  // URL — keeps proof links small and gives Zoho/order records stable links.
  async function ensureArtworkUrls() {
    const out = { front: '', back: '' };
    for (const side of ['front', 'back']) {
      const a = state.artwork[side];
      if (side === 'back' && !backEnabled()) continue;
      if (!a.dataUrl) continue;
      let r2 = typeof a.proofUrl === 'string' && a.proofUrl.startsWith('http') ? a.proofUrl : '';
      if (!r2) {
        const file = await fileForUpload(state.artworkFiles[side] || a.rawDataUrl || a.dataUrl, a.name || side + '-artwork.png');
        r2 = file ? await uploadToWorker(file, 'studio-artwork/' + side + '/') : '';
        if (r2) a.proofUrl = r2;
      }
      out[side] = r2;
    }
    return out;
  }

  /* ---------- wholesale ---------- */
  const wholesaleModal = $('#dc-wholesale-modal');
  const wholesaleForm = $('#dc-wholesale-form');
  const wholesaleQty = () => Math.max(1, +$('#dc-wh-qty')?.value || 1);

  function wholesaleDetails(qty, proofLink = '') {
    const variant = resolveVariant();
    const details = [
      ['Product', config.productTitle],
      ['SKU', variant.sku || ''],
      ['Finish', state.finish],
      ['Quantity', qty],
      ['Front artwork', state.artwork.front.dataUrl ? (state.artwork.front.name || 'Uploaded') : 'None'],
      ['Back artwork', backEnabled() && state.artwork.back.dataUrl ? (state.artwork.back.name || 'Uploaded') : 'None'],
      ['Front text', sideHasText('front') ? '“' + state.text.front.text + '” (' + state.text.front.fontLabel + ')' : 'None'],
      ...(backEnabled() ? [['Back text', sideHasText('back') ? '“' + state.text.back.text + '” (' + state.text.back.fontLabel + ')' : 'None']] : []),
    ];
    if (proofLink) details.push(['Design link', proofLink]);
    return details;
  }
  const wholesaleDetailsText = (qty, proofLink) => wholesaleDetails(qty, proofLink).map(([k, v]) => k + ': ' + v).join('\n');

  async function wholesaleMailto() {
    const btn = $('#dc-wholesale-mailto');
    if (btn) btn.disabled = true;
    let proofLink = '';
    try {
      await ensureArtworkUrls();
      proofLink = await storeProofLink();
    } catch (e) {}
    const subject = encodeURIComponent('Bulk / wholesale inquiry — ' + config.productTitle);
    const body = encodeURIComponent(wholesaleDetailsText(wholesaleQty(), proofLink) + '\n');
    const email = config.wholesaleEmail || 'info@' + location.hostname;
    if (btn) btn.disabled = false;
    location.href = 'mailto:' + email + '?subject=' + subject + '&body=' + body;
  }
  $('#dc-wholesale-mailto')?.addEventListener('click', wholesaleMailto);

  if (wholesaleModal) {
    let lastFocus = null;
    const formView = $('#dc-wholesale-form-view');
    const successView = $('#dc-wholesale-success');
    const statusEl = $('#dc-wholesale-status');
    const submitBtn = $('#dc-wholesale-submit');
    const fileInput = $('#dc-wh-file');
    const fileStatus = $('#dc-wh-file-status');
    const setStatus = (msg, type) => {
      statusEl.hidden = !msg;
      statusEl.textContent = msg || '';
      if (type) statusEl.dataset.type = type; else delete statusEl.dataset.type;
    };
    const validateWholesaleFile = () => {
      const file = fileInput?.files?.[0];
      if (!file) return true;
      const error = file.size > MAX_UPLOAD_BYTES
        ? 'File is larger than 12 MB — please choose a smaller file.'
        : !WHOLESALE_FILE_TYPES.has(file.type)
          ? 'Please choose a PNG, JPG, WebP, SVG, or PDF file.'
          : '';
      if (fileStatus) fileStatus.textContent = error || 'PNG, JPG, WebP, SVG, or PDF · up to 12 MB';
      if (error) fileInput.value = '';
      return !error;
    };
    fileInput?.addEventListener('change', validateWholesaleFile);

    function fillSummary() {
      const variant = resolveVariant();
      const textRow = ([key, value]) => {
        const item = document.createElement('div');
        item.className = 'summary-item';
        const dt = document.createElement('dt'); dt.textContent = key;
        const dd = document.createElement('dd'); dd.textContent = value;
        item.append(dt, dd);
        return item;
      };
      const dl = $('#dc-wholesale-summary');
      const qtyItem = $('#dc-wh-qty-item');
      // rebuild everything except the live quantity stepper, which stays in place
      dl.querySelectorAll(':scope > .summary-item:not(#dc-wh-qty-item)').forEach((el) => el.remove());
      qtyItem.before(textRow(['Product', config.productTitle + ' – ' + state.finish + ' · ' + (variant.sku || '—')]));
      const rest = [textRow(['Finish', state.finish])];
      if (state.profile) rest.push(textRow(['Profile', state.profile]));
      rest.push(sideSummaryItem('front', true), sideSummaryItem('back', true));
      qtyItem.after(...rest);
    }

    const reviewStep = $('#dc-wholesale-review-step');
    const detailsStep = $('#dc-wholesale-details-step');
    const showStep = (step) => {
      const review = step === 'review';
      if (reviewStep) reviewStep.hidden = !review;
      if (detailsStep) detailsStep.hidden = review;
      wholesaleModal.setAttribute('aria-labelledby', review ? 'dc-wholesale-review-title' : 'dc-wholesale-modal-title');
    };
    $('#dc-wholesale-continue')?.addEventListener('click', () => {
      const ctx = $('#dc-wholesale-context');
      if (ctx) ctx.textContent = wholesaleQty() + ' × ' + config.productTitle + ' · ' + state.finish;
      showStep('details');
      $('#dc-wh-first-name')?.focus();
    });
    $('#dc-wholesale-step-back')?.addEventListener('click', () => { showStep('review'); $('#dc-wholesale-continue')?.focus(); });

    let closeTimer = null;
    const openModal = () => {
      clearTimeout(closeTimer);
      lastFocus = document.activeElement;
      $('#dc-wh-qty').value = Math.max(state.quantity, config.bulkThreshold || 1);
      fillSummary();
      formView.hidden = false;
      successView.hidden = true;
      submitBtn.disabled = false;
      setStatus('');
      showStep('review');
      wholesaleModal.hidden = false;
      document.documentElement.classList.add('dc-modal-open');
      // force a style flush so the pop-in transition runs
      void wholesaleModal.offsetHeight;
      wholesaleModal.classList.add('is-open');
      $('#dc-wholesale-continue')?.focus();
    };
    const closeModal = () => {
      wholesaleModal.classList.remove('is-open');
      document.documentElement.classList.remove('dc-modal-open');
      closeTimer = setTimeout(() => { wholesaleModal.hidden = true; }, 300);
      lastFocus?.focus();
    };

    $('#dc-wholesale-toggle')?.addEventListener('click', openModal);
    $('#dc-order-wholesale-inquiry')?.addEventListener('click', openModal);
    wholesaleModal.querySelectorAll('[data-close-modal]').forEach((el) => el.addEventListener('click', closeModal));
    document.addEventListener('keydown', (e) => {
      if (wholesaleModal.hidden) return;
      if (e.key === 'Escape') { closeModal(); return; }
      if (e.key !== 'Tab') return;
      // keep focus inside the dialog
      const focusables = Array.from(wholesaleModal.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      )).filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
      else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
    });

    wholesaleForm?.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (wholesaleForm.elements.dc_website.value) return;

      const workerUrl = workerBaseUrl();
      if (!workerUrl) {
        setStatus('This form is not configured. Please contact us by email.', 'error');
        return;
      }

      if (!validateWholesaleFile()) {
        setStatus('Please choose a valid attachment before sending.', 'error');
        return;
      }
      submitBtn.disabled = true;
      setStatus('Uploading artwork…');

      try {
        // Upload front/back studio artwork if present and store the R2 URLs
        // as proofUrl so the proof link stays small.
        const { front: frontArtworkUrl, back: backArtworkUrl } = await ensureArtworkUrls();

        // Upload optional extra attachment from the wholesale modal.
        const extraFile = fileInput?.files?.[0];
        const extraFileUrl = extraFile
          ? await uploadToWorker(extraFile, 'wholesale-attachments/')
          : '';

        console.info('[DashClip] Cloudflare uploads complete', {
          frontArtworkUrl: frontArtworkUrl || null,
          backArtworkUrl: backArtworkUrl || null,
          additionalFileUrl: extraFileUrl || null,
        });

        const notes = wholesaleForm.elements.dc_notes.value.trim();
        const qty = wholesaleQty();
        const variant = resolveVariant();

        // Build the Zoho inquiry payload.
        const payload = new FormData();
        payload.append('contact[first_name]', wholesaleForm.elements['contact[first_name]'].value);
        payload.append('contact[last_name]', wholesaleForm.elements['contact[last_name]'].value);
        payload.append('contact[email]', wholesaleForm.elements['contact[email]'].value);
        payload.append('contact[phone]', wholesaleForm.elements['contact[phone]'].value);
        payload.append('contact[company]', wholesaleForm.elements['contact[company]'].value);
        payload.append('contact[quantity]', String(qty));
        payload.append('contact[product]', config.productTitle + ' · ' + (variant.sku || '—'));
        payload.append('contact[finish]', state.finish + (state.profile ? ' · ' + state.profile : ''));
        payload.append('contact[personalization]', ['front', 'back']
          .filter(sideHasText)
          .map((sd) => sd + ': ' + state.text[sd].text)
          .join(' / '));
        // Keep the proof link out of the body — it embeds base64 artwork and
        // oversized field values make Zoho silently drop the whole submission.
        const detailsText = wholesaleDetails(qty)
          .filter(([key]) => key !== 'Design link')
          .map(([key, value]) => key + ': ' + value)
          .join('\n');
        payload.append(
          'contact[body]',
          (notes ? 'Message:\n' + notes + '\n\n' : '') + 'Order details:\n' + detailsText
        );
        if (frontArtworkUrl) payload.append('contact[front_artwork_url]', frontArtworkUrl);
        if (backArtworkUrl) payload.append('contact[back_artwork_url]', backArtworkUrl);
        if (extraFileUrl) payload.append('contact[additional_file_url]', extraFileUrl);
        // Store the design proof URL in R2 for a short redirect link.
        const proofLink = await storeProofLink();
        if (proofLink) payload.append('contact[design_proof_link]', proofLink);

        setStatus('Sending your inquiry…');
        console.info('[DashClip] Sending inquiry with Cloudflare links', {
          frontArtworkUrl: frontArtworkUrl || null,
          backArtworkUrl: backArtworkUrl || null,
          additionalFileUrl: extraFileUrl || null,
        });
        const res = await fetch(workerUrl + '/inquiry', {
          method: 'POST',
          body: payload,
        });
        const data = await res.json().catch(() => null);
        console.info('[DashClip] Inquiry response', { status: res.status, body: data });
        if (!res.ok || data?.success === false) {
          throw new Error((data && (data.error || data.zohoResponse)) || 'Send failed');
        }

        formView.hidden = true;
        successView.hidden = false;
        successView.querySelector('button')?.focus();
      } catch (err) {
        console.error('[DashClip] Wholesale inquiry failed:', err);
        setStatus('We couldn’t send your inquiry. Please try again, or use the email option below.', 'error');
        submitBtn.disabled = false;
      }
    });
  } else {
    $('#dc-order-wholesale-inquiry')?.addEventListener('click', wholesaleMailto);
  }

  /* ---------- review / approve ---------- */
  function lockOrderStep() {
    const locked = $('#dc-order-locked');
    const step = $('#dc-order-step');
    if (locked) locked.hidden = false;
    if (step) step.hidden = true;
    const review = $('#dc-design-review'), approved = $('#dc-design-approved');
    if (review) review.hidden = false;
    if (approved) approved.hidden = true;
    const r = $('#dc-step-review'), o = $('#dc-step-order');
    if (r) { r.classList.add('is-current'); r.classList.remove('is-done'); }
    if (o) { o.classList.add('is-locked'); o.classList.remove('is-current', 'is-done'); }
  }
  function unlockOrderStep() {
    const locked = $('#dc-order-locked');
    if (locked) locked.hidden = true;
    const review = $('#dc-design-review'), approved = $('#dc-design-approved');
    if (review) review.hidden = true;
    if (approved) approved.hidden = false;
    const r = $('#dc-step-review'), o = $('#dc-step-order');
    if (r) { r.classList.remove('is-current'); r.classList.add('is-done'); }
    if (o) { o.classList.remove('is-locked', 'is-done'); o.classList.add('is-current'); }
  }

  function summaryPairs() {
    const variant = resolveVariant();
    const pairs = [
      ['Product', config.productTitle],
      ['SKU', variant.sku || '—'],
      ['Finish', state.finish],
    ];
    if (state.profile) pairs.push(['Profile', state.profile]);
    if (supportsArtwork) {
      pairs.push(['Front artwork', state.artwork.front.dataUrl ? (state.artwork.front.name || 'Uploaded') : 'None']);
      if (backEnabled()) {
        pairs.push(['Back artwork', state.artwork.back.dataUrl ? (state.artwork.back.name || 'Uploaded') : 'None']);
      }
    }
    if (supportsText) {
      if (sideHasText('front')) pairs.push(['Front text', '“' + state.text.front.text + '” — ' + state.text.front.fontLabel + ' lettering']);
      if (sideHasText('back')) pairs.push(['Back text', '“' + state.text.back.text + '” — ' + state.text.back.fontLabel + ' lettering']);
    }
    pairs.push(['Quantity', String(state.quantity)]);
    pairs.push(['Per item', money(variant.price)]);
    return pairs;
  }

  function hasUsedArtwork() {
    return supportsArtwork && !!(state.artwork.front.dataUrl ||
      (backEnabled() && state.artwork.back.dataUrl));
  }

  const FONT_FAMILIES = {
    Canela: "'CanelaArabic', 'Canela', Georgia, serif",
    Modern: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    Allura: "'Allura', cursive",
    Heritage: "'Libre Baskerville', Georgia, serif",
    Mono: "'IBM Plex Mono', monospace",
  };

  function sideSummaryItem(side, withLabel) {
    const item = document.createElement('div');
    item.className = 'summary-item summary-item--side';
    if (withLabel) {
      const dt = document.createElement('dt');
      dt.textContent = side === 'front' ? 'Front' : 'Back';
      item.append(dt);
    }
    const dd = document.createElement('dd');

    const enabled = side === 'front' || backEnabled();
    const art = enabled && state.artwork[side].dataUrl ? state.artwork[side] : null;
    const sideText = state.text[side];
    const hasText = enabled && sideText.enabled && sideText.text && sideText.text.trim();

    const lines = document.createElement('div');
    lines.className = 'summary-lines';
    if (!enabled) {
      const na = document.createElement('span');
      na.textContent = 'Not available on this product';
      lines.append(na);
      dd.append(lines);
      item.append(dd);
      return item;
    }

    if (art) {
      const img = document.createElement('img');
      img.className = 'summary-art';
      img.src = art.dataUrl;
      img.alt = (side === 'front' ? 'Front' : 'Back') + ' artwork: ' + (art.name || 'Uploaded');
      dd.append(img);
    }

    if (!withLabel) {
      const nameLine = document.createElement('span');
      nameLine.textContent = side === 'front' ? 'Front' : 'Back';
      lines.append(nameLine);
    }
    const artLine = document.createElement('span');
    artLine.textContent = art ? (art.name || 'Uploaded') : 'No logo customization';
    if (art) artLine.title = art.name || 'Uploaded';
    lines.append(artLine);
    const detailLine = document.createElement('span');
    const parts = [];
    if (art) parts.push(art.size + '% size');
    if (hasText) {
      parts.push('“' + sideText.text + '” · ' + sideText.fontLabel);
      if (FONT_FAMILIES[sideText.font]) detailLine.style.fontFamily = FONT_FAMILIES[sideText.font];
    } else {
      parts.push('No text');
    }
    detailLine.textContent = parts.join(' · ');
    lines.append(detailLine);
    dd.append(lines);

    item.append(dd);
    return item;
  }

  $('#dc-review')?.addEventListener('click', () => {
    const variant = resolveVariant();
    const facts = [
      ['Finish', state.finish],
      ['Quantity', state.quantity + ' · ' + (state.quantity >= config.bulkThreshold ? 'Bulk inquiry' : 'Checkout')],
    ];
    if (state.profile) facts.push(['Profile', state.profile]);
    const dl = $('#dc-summary');
    dl.replaceChildren();
    const makeItem = (key, value, cls) => {
      const item = document.createElement('div');
      item.className = 'summary-item' + (cls ? ' ' + cls : '');
      const dt = document.createElement('dt'); dt.textContent = key;
      const dd = document.createElement('dd'); dd.textContent = value;
      item.append(dt, dd);
      return item;
    };
    const productItem = document.createElement('div');
    productItem.className = 'summary-item summary-item--product';
    const productDd = document.createElement('dd');
    const productName = document.createElement('span');
    productName.textContent = config.productTitle;
    productDd.append(productName);
    if (variant.sku) {
      const sku = document.createElement('small');
      sku.textContent = 'SKU ' + variant.sku;
      productDd.append(sku);
    }
    productItem.append(productDd);
    dl.append(productItem);
    const factsWrap = document.createElement('div');
    factsWrap.className = 'summary-facts';
    for (const [key, value] of facts) factsWrap.append(makeItem(key, value));
    dl.append(factsWrap);
    const sides = document.createElement('div');
    sides.className = 'summary-sides';
    const sidesLabel = document.createElement('dt');
    sidesLabel.textContent = 'Artwork';
    sides.append(sidesLabel, sideSummaryItem('front'), sideSummaryItem('back'));
    dl.append(sides);
    $('#dc-disclaimer-row').hidden = !hasUsedArtwork();
    const logoNote = config.rules.logoIncluded
      ? 'Logo customization is included in the price.'
      : 'Logo customization is priced in the selected variant.';
    const textNote = (sideHasText('front') || sideHasText('back'))
      ? 'Text personalization is included in the selected price.'
      : 'No text personalization charge.';
    $('#dc-summary-note').textContent = logoNote + ' ' + textNote;
    $('#dc-approval-status').textContent = config.sectionId === 'preview'
      ? 'Demonstration only. No order is placed.'
      : 'No order is submitted until checkout.';
    lockOrderStep();
    $('#dc-artwork-disclaimer').checked = false;
    syncApproveButton();
    $('#dc-checkout-flow').hidden = false;
    $('#dc-design-review').focus();
    $('#dc-design-review').scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  $('#dc-edit')?.addEventListener('click', () => { $('#dc-checkout-flow').hidden = true; });

  function syncApproveButton() {
    const btn = $('#dc-approve');
    if (btn) btn.disabled = hasUsedArtwork() && !$('#dc-artwork-disclaimer').checked;
  }
  $('#dc-artwork-disclaimer')?.addEventListener('change', () => {
    syncApproveButton();
    if ($('#dc-artwork-disclaimer').checked) $('#dc-approval-status').textContent = 'No order is submitted until checkout.';
  });

  $('#dc-approve')?.addEventListener('click', () => {
    if (hasUsedArtwork() && !$('#dc-artwork-disclaimer').checked) {
      $('#dc-approval-status').textContent = 'Please confirm the artwork rights disclaimer to continue.';
      return;
    }
    $('#dc-approval-status').textContent = '';
    const dl = $('#dc-order-summary');
    dl.innerHTML = '';
    const variant = resolveVariant();
    for (const [k, v] of summaryPairs()) {
      if (['Product', 'SKU', 'Finish'].includes(k)) continue;
      const row = document.createElement('div'); row.className = 'order-row';
      const dt = document.createElement('dt'); dt.textContent = k;
      const dd = document.createElement('dd'); dd.textContent = v;
      row.append(dt, dd);
      dl.append(row);
    }
    $('#dc-os-swatch').style.background = swatchColorOf(variant) || '#888888';
    $('#dc-os-name').textContent = [config.productTitle, state.finish].filter(Boolean).join(' · ');
    $('#dc-os-detail').textContent = variant.sku ? 'SKU ' + variant.sku : '';
    const base = baseVariant();
    const flags = desiredFlags();
    const qty = state.quantity;
    $('#dc-product-price-label').textContent = config.productTitle + ' × ' + qty;
    $('#dc-product-price').textContent = money(base.price * qty);
    const diff = variant.price - base.price;
    const logoCharge = !config.rules.logoIncluded && flags.logo ? config.rules.logoUpcharge : 0;
    const textCharge = flags.name ? Math.max(diff - logoCharge, 0) : 0;
    const dsCharge = Math.max(diff - logoCharge - textCharge, 0);
    $('#dc-order-text-row').hidden = !supportsText;
    $('#dc-engraving-cost').textContent = textCharge > 0 ? money(textCharge * qty) : 'No charge';
    $('#dc-order-logo-included-row').hidden = !(config.rules.logoIncluded && supportsArtwork && !config.priceOnRequest);
    $('#dc-order-double-sided-row').hidden = !state.doubleSided;
    $('#dc-order-double-sided-cost').textContent = money(dsCharge * qty);
    $('#dc-order-logo-row').hidden = logoCharge === 0;
    $('#dc-order-logo-cost').textContent = money(logoCharge * qty);
    $('#dc-order-subtotal').textContent = money(variant.price * qty);
    const bulk = qty >= config.bulkThreshold;
    $('#dc-order-total-title').textContent = bulk ? 'Request a quote' : 'Order details';
    $('#dc-order-wholesale-inquiry').hidden = !bulk;
    const wholesaleStatus = $('#dc-order-wholesale-status');
    wholesaleStatus.hidden = !bulk;
    wholesaleStatus.textContent = bulk
      ? 'Orders of ' + config.bulkThreshold + ' or more are handled by our wholesale team.'
      : '';
    $('#dc-add-to-cart').hidden = bulk;
    $('#dc-checkout').hidden = bulk;
    $('#dc-pricing-note').textContent = bulk
      ? 'Bulk orders are quoted by our team — send the inquiry and we’ll reply within 24 hours.'
      : 'Shipping and tax are additional.';
    const sidesWithArt = ['front', 'back'].filter((sd) => (sd === 'front' || backEnabled()) && state.artwork[sd].dataUrl);
    $('#dc-approved-detail').textContent = sidesWithArt.length === 2 ? 'Front and back artwork confirmed'
      : sidesWithArt.length === 1 ? (sidesWithArt[0] === 'front' ? 'Front' : 'Back') + ' artwork confirmed'
      : 'Design confirmed';
    unlockOrderStep();
    $('#dc-order-step').hidden = false;
    $('#dc-order-step').focus();
    $('#dc-order-step').scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  $('#dc-order-back')?.addEventListener('click', () => { lockOrderStep(); });

  /* ---------- cart ---------- */
  async function addToCart(btn, goCheckout) {
    btn.disabled = true;
    $('#dc-integration-note').textContent = 'Adding to cart…';
    try {
      const variant = resolveVariant();
      const fd = new FormData();
      fd.append('id', variant.id);
      fd.append('quantity', state.quantity);
      fd.append('properties[Finish]', state.finish);
      if (state.profile) fd.append('properties[Profile]', state.profile);
      for (const side of ['front', 'back']) {
        if (!sideHasText(side)) continue;
        const label = side === 'front' ? 'Front' : 'Back';
        fd.append('properties[' + label + ' text]', state.text[side].text);
        fd.append('properties[' + label + ' lettering]', state.text[side].fontLabel);
      }
      if (supportsBack) {
        const both = state.doubleSided ||
          (config.rules.doubleSidedUpcharge === 0 && !!state.artwork.back.dataUrl);
        fd.append('properties[Double-sided]', both ? 'Yes' : 'No');
      }
      for (const side of ['front', 'back']) {
        if (side === 'back' && !backEnabled()) continue;
        const a = state.artwork[side];
        if (!a.dataUrl) continue;
        fd.append('properties[' + side + ' artwork size]', a.size + '%');
        if (a.invert) fd.append('properties[' + side + ' artwork inverted]', 'Yes');
        try {
          const file = await fileForUpload(
            state.artworkFiles[side] || a.rawDataUrl || a.dataUrl,
            a.name || side + '-artwork.png'
          );
          if (file) fd.append('properties[' + side + ' artwork]', file, file.name);
        } catch (e) {}
      }
      const ref = $('#dc-proof-title').value.trim();
      if (ref) fd.append('properties[Design reference]', ref);

      // Attach R2 artwork links + a short design-proof link as hidden
      // properties so the checkout pixel → Worker /order → Zoho sync can use
      // them. Best-effort: never block add to cart.
      if (workerBaseUrl()) {
        try {
          const { front, back } = await ensureArtworkUrls();
          if (front) fd.append('properties[_front artwork url]', front);
          if (back) fd.append('properties[_back artwork url]', back);
          const proofLink = await storeProofLink();
          if (proofLink) fd.append('properties[_design proof]', proofLink);
        } catch (e) { /* proof enrichment is optional */ }
      }

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart) {
        fd.append('sections', cart.getSectionsToRender().map((s) => s.id));
        fd.append('sections_url', window.location.pathname);
        cart.setActiveElement(btn);
      }
      const res = await fetch(config.routes.cartAdd + '.js', { method: 'POST', body: fd });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.status) throw new Error(json.description || json.message || 'Could not add to cart');
      if (goCheckout) {
        $('#dc-integration-note').textContent = 'Added — heading to checkout…';
        window.location.href = config.routes.checkout || '/checkout';
        return;
      }
      if (!cart) {
        $('#dc-integration-note').textContent = 'Added — opening your cart…';
        window.location.href = config.routes.cart;
        return;
      }
      $('#dc-integration-note').textContent = 'Added to your cart.';
      if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
      cart.renderContents(json);
      btn.disabled = false;
    } catch (err) {
      $('#dc-integration-note').textContent = err.message || 'Something went wrong adding to cart.';
      btn.disabled = false;
    }
  }
  $('#dc-add-to-cart')?.addEventListener('click', () => addToCart($('#dc-add-to-cart'), false));
  $('#dc-checkout')?.addEventListener('click', () => addToCart($('#dc-checkout'), true));

  const shareDetails = $('#dc-share-options');
  shareDetails?.addEventListener('toggle', () => {
    shareDetails.querySelector('summary').setAttribute('aria-expanded', String(shareDetails.open));
  });

  /* ---------- proofs ---------- */
  function serializeDesign() {
    // downscale artwork for URL embedding
    const slim = (a) => {
      if (!a.dataUrl) return null;
      // prefer the original source so invert can be toggled after hydration —
      // fall back to the mask thumbnail for oversized uploads
      const d = (a.rawDataUrl && a.rawDataUrl.length < 60000)
        ? a.rawDataUrl
        : (a.proofUrl || (a.dataUrl.length < 60000 ? a.dataUrl : null));
      return { d, n: a.name, i: a.invert, s: a.size };
    };
    return {
      v: 2,
      p: config.productHandle,
      f: state.finish,
      pr: state.profile,
      ds: state.doubleSided,
      s: state.side,
      text: { front: { ...state.text.front }, back: { ...state.text.back } },
      q: state.quantity,
      af: slim(state.artwork.front),
      ab: slim(state.artwork.back),
      ref: $('#dc-proof-title')?.value.trim() || '',
    };
  }

  function buildProofUrl() {
    const data = btoa(unescape(encodeURIComponent(JSON.stringify(serializeDesign()))));
    return location.origin + config.productUrl + '?design=' + encodeURIComponent(data);
  }

  // Prefer the short R2 redirect link (stores the full ?design= URL server-side);
  // falls back to the self-contained long URL when the Worker is unreachable.
  async function shareableProofUrl() {
    if (workerBaseUrl()) {
      try {
        await ensureArtworkUrls();
        const short = await storeProofLink();
        if (short) return short;
      } catch (e) { /* fall through to the long URL */ }
    }
    return buildProofUrl();
  }

  $('#dc-save-proof')?.addEventListener('click', async () => {
    $('#dc-proof-status').textContent = 'Creating share link…';
    const url = await shareableProofUrl();
    $('#dc-saved-link-wrap').hidden = false;
    $('#dc-saved-link').value = url;
    try {
      await navigator.clipboard.writeText(url);
      $('#dc-proof-status').textContent = 'Link copied — anyone with it can view this design.';
    } catch {
      $('#dc-proof-status').textContent = 'Copy the link below to share this design.';
    }
  });

  $('#dc-email-proof')?.addEventListener('click', async () => {
    const url = await shareableProofUrl();
    const subject = encodeURIComponent('DashClip design proof — ' + config.productTitle);
    const body = encodeURIComponent('View this design: ' + url + '\n\n' +
      summaryPairs().map(([k, v]) => k + ': ' + v).join('\n'));
    location.href = 'mailto:?subject=' + subject + '&body=' + body;
  });

  $('#dc-download')?.addEventListener('click', async () => {
    const statusEl = $('#dc-proof-status');
    const d3 = window.dashclip3d;
    if (!d3 || !d3.capture) {
      statusEl.textContent = 'Preview is still loading — try again in a moment.';
      return;
    }
    const shots = [{ side: 'Front', url: d3.capture('front') }];
    const backHasContent = backEnabled() &&
      (state.artwork.back.dataUrl || sideHasText('back'));
    if (backHasContent) shots.push({ side: 'Back', url: d3.capture('back') });

    let url = shots[0].url;
    if (shots.length > 1) {
      try {
        statusEl.textContent = 'Composing front and back proof…';
        const imgs = await Promise.all(shots.map((s) => new Promise((res, rej) => {
          const img = new Image();
          img.onload = () => res(img);
          img.onerror = rej;
          img.src = s.url;
        })));
        const pad = 32, gap = 24, labelH = 44;
        const cellW = Math.max(...imgs.map((i) => i.width));
        const cellH = Math.max(...imgs.map((i) => i.height));
        const cv = document.createElement('canvas');
        cv.width = pad * 2 + cellW * imgs.length + gap * (imgs.length - 1);
        cv.height = pad * 2 + cellH + labelH;
        const ctx = cv.getContext('2d');
        // match the stage's soft radial backdrop
        const grad = ctx.createRadialGradient(cv.width * 0.45, cv.height * 0.35, 0, cv.width * 0.5, cv.height * 0.5, cv.width * 0.75);
        grad.addColorStop(0, '#ffffff');
        grad.addColorStop(0.54, '#f0f2f4');
        grad.addColorStop(1, '#dbe0e6');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, cv.width, cv.height);
        imgs.forEach((img, i) => {
          const x = pad + i * (cellW + gap);
          ctx.drawImage(img, x, pad, cellW, cellH);
          ctx.fillStyle = '#10243d';
          ctx.font = '600 18px ' + (getComputedStyle(document.body).getPropertyValue('--font-body-family') || 'sans-serif');
          ctx.textAlign = 'center';
          ctx.fillText(shots[i].side.toUpperCase(), x + cellW / 2, pad + cellH + labelH - 14);
        });
        url = cv.toDataURL('image/png');
      } catch (err) {
        url = shots[0].url;
      }
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = (resolveVariant().sku || 'dashclip-design') + '-proof.png';
    a.click();
    statusEl.textContent = backHasContent
      ? 'Downloaded a proof showing the front and back.'
      : 'Proof image downloaded.';
  });

  /* ---------- hydration ---------- */
  function hydrate(design) {
    if (!design) return;
    if (design.p && !shared.isFamilyPage) {
      const idx = products.findIndex((p) => p.productHandle === design.p);
      if (idx >= 0 && idx !== activeIndex) activateProduct(idx);
    }
    if (design.f && finishes.includes(design.f)) state.finish = design.f;
    if (design.pr && profileValues.includes(design.pr)) state.profile = design.pr;
    state.doubleSided = supportsBack && !!design.ds;
    state.side = backEnabled() && design.s === 'back' ? 'back' : 'front';
    for (const sd of ['front', 'back']) {
      const d = (design.text || {})[sd] || {};
      const t = state.text[sd];
      t.enabled = supportsText && !!d.enabled;
      t.text = supportsText ? d.text || '' : '';
      if (d.font) { t.font = d.font; t.fontLabel = d.fontLabel || d.font; }
    }
    // legacy proof links carried a single text + side — map it onto that side
    if (design.t) {
      const sd = backEnabled() && design.ts === 'back' ? 'back' : 'front';
      const t = state.text[sd];
      t.enabled = supportsText;
      t.text = supportsText ? design.t : '';
      if (design.fo) { t.font = design.fo; t.fontLabel = design.fl || design.fo; }
    }
    state.quantity = design.q || 1;
    if (design.ref && $('#dc-proof-title')) $('#dc-proof-title').value = design.ref;
    for (const [key, side] of [['af', 'front'], ['ab', 'back']]) {
      if (!supportsArtwork || (side === 'back' && !supportsBack)) continue;
      const a = design[key];
      if (a && a.d) {
        state.artwork[side] = {
          dataUrl: null,
          rawDataUrl: a.d,
          proofUrl: /^https?:\/\//i.test(a.d) ? a.d : null,
          name: a.n,
          invert: !!a.i,
          size: a.s || 64,
        };
        applyArtwork(a.d, a.n, side, !!a.i);
      }
    }
    const banner = $('#dc-proof-banner');
    if (banner) {
      banner.hidden = false;
      banner.textContent = design.ref
        ? 'Viewing a saved design — “' + design.ref + '”. Edit anything to make it yours.'
        : 'Viewing a saved design. Edit anything to make it yours.';
    }
    syncUI();
  }

  let initialDesign = null;
  const urlParams = new URLSearchParams(location.search);
  try {
    const encoded = urlParams.get('design');
    if (encoded) initialDesign = JSON.parse(decodeURIComponent(escape(atob(decodeURIComponent(encoded)))));
    else {
      const handoff = sessionStorage.getItem('dashclip-handoff');
      if (handoff) { sessionStorage.removeItem('dashclip-handoff'); initialDesign = JSON.parse(handoff); }
    }
  } catch (e) { /* malformed link — ignore */ }

  // initial product: on a family-product URL the page product always wins;
  // otherwise the design's product, else this page's own product,
  // else the configured default, else the first tab
  let initialIndex = -1;
  if (shared.isFamilyPage) initialIndex = products.findIndex((p) => p.productHandle === shared.activeHandle);
  if (initialIndex < 0 && initialDesign) initialIndex = products.findIndex((p) => p.productHandle === initialDesign.p);
  if (initialIndex < 0) initialIndex = products.findIndex((p) => p.productHandle === shared.activeHandle);
  if (initialIndex < 0 && shared.defaultProductHandle) initialIndex = products.findIndex((p) => p.productHandle === shared.defaultProductHandle);
  if (initialIndex < 0) initialIndex = 0;
  activateProduct(initialIndex);

  // deep links: ?variant=<id> (Shopify-style), ?finish=<option value>, ?profile=<value>.
  // Applied before hydrate() so a ?design= link still wins when both are present.
  const wantVariant = urlParams.get('variant');
  const wantFinish = urlParams.get('finish');
  const wantProfile = urlParams.get('profile');
  if (wantVariant) {
    const id = String(wantVariant);
    let found = config.variants.find((v) => String(v.id) === id);
    // family-page URLs keep their own product; elsewhere a variant id can switch tabs
    if (!found && !shared.isFamilyPage) {
      const owner = products.findIndex((p) => (p.variants || []).some((v) => String(v.id) === id));
      if (owner >= 0) {
        activateProduct(owner);
        found = config.variants.find((v) => String(v.id) === id);
      }
    }
    if (found) {
      state.finish = found.options[0];
      if (found.options[2] && profileValues.includes(found.options[2])) state.profile = found.options[2];
    }
  }
  if (wantFinish) {
    const match = finishes.find((f) => String(f).toLowerCase() === wantFinish.toLowerCase());
    if (match) state.finish = match;
  }
  if (wantProfile && profileValues.includes(wantProfile)) state.profile = wantProfile;
  if (wantVariant || wantFinish || wantProfile) {
    // selects render once in renderProductChrome and don't re-sync in syncUI
    const ws = $('#dc-wallet-select');
    if (ws && config.finishSelector === 'select') ws.value = state.finish;
    const th = $('#dc-marker-thickness');
    if (th && state.profile) th.value = state.profile;
    syncUI();
  }

  hydrate(initialDesign);

  // re-fit the stage word when the stage resizes or webfonts finish loading
  const stageEl = $('.product-stage');
  if (stageEl && typeof ResizeObserver !== 'undefined') new ResizeObserver(fitStageWord).observe(stageEl);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitStageWord);

  // family tabs switch products in-page; real hrefs remain as no-JS/SEO fallback
  $$('.category-tabs .family-tab').forEach((tab) =>
    tab.addEventListener('click', (e) => {
      // stash the design in case the link does navigate away
      try { sessionStorage.setItem('dashclip-handoff', JSON.stringify(serializeDesign())); } catch (err) {}
      const idx = tabIndex(tab);
      if (idx === -1) return;
      e.preventDefault();
      if (idx !== activeIndex) activateProduct(idx);
    })
  );

  // viewer controls are handled by the 3D module; expose state getter
  window.dashclipStudio = { state, get config() { return config; }, products, resolveVariant, syncUI };

  syncUI();
})();
